import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeader, parseResponse } from '../api/helpers';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.btrix.in';

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid #e2e8f0', fontSize: 13, outline: 'none',
  color: '#1e293b', background: '#f8fafc', boxSizing: 'border-box'
};
const labelStyle = {
  fontSize: 11, fontWeight: 600, color: '#475569',
  display: 'block', marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: 0.5
};
const sectionTitle = (title) => (
  <div style={{ fontSize: 13, fontWeight: 700, color: '#0284c7', marginBottom: 12,
    paddingBottom: 8, borderBottom: '2px solid #e0f2fe' }}>{title}</div>
);

// ── Add Visit Modal ───────────────────────────────────
function AddVisitModal({ patient, onClose, onSuccess, user }) {
  const [form, setForm] = useState({
    doctor_name: '', visit_date: new Date().toISOString().split('T')[0],
    chief_complaint: '', diagnosis: '', notes: '',
    bp: '', temp: '', weight: '', pulse: '', spo2: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const headers = await getAuthHeader();
      const res = await axios.post(`${API_URL}/visits`, {
        ...form,
        patient_id:  patient.patient_id,
        hospital_id: patient.hospital_id,
      }, { headers });
      const data = parseResponse(res);
      onSuccess(data);
    } catch (err) {
      setError('Failed to save visit');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} className="modal-overlay">
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 600, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Record Visit</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 20 }}>✕</span>
        </div>
        {error && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Doctor Name</label>
              <input style={inputStyle} placeholder="Dr. Name" required
                value={form.doctor_name} onChange={e => setForm({...form, doctor_name: e.target.value})} />
            </div>
            <div>
              <label style={labelStyle}>Visit Date</label>
              <input style={inputStyle} type="date" required
                value={form.visit_date} onChange={e => setForm({...form, visit_date: e.target.value})} />
            </div>
          </div>

          {sectionTitle('Vitals')}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
            {[
              { key: 'bp',     placeholder: 'BP (mmHg)',  label: 'Blood Pressure' },
              { key: 'temp',   placeholder: 'Temp (°F)',  label: 'Temperature' },
              { key: 'weight', placeholder: 'Wt (kg)',    label: 'Weight' },
              { key: 'pulse',  placeholder: 'Pulse/min',  label: 'Pulse' },
              { key: 'spo2',   placeholder: 'SpO2 (%)',   label: 'SpO2' },
            ].map(v => (
              <div key={v.key}>
                <label style={labelStyle}>{v.label}</label>
                <input style={inputStyle} placeholder={v.placeholder}
                  value={form[v.key]} onChange={e => setForm({...form, [v.key]: e.target.value})} />
              </div>
            ))}
          </div>

          {sectionTitle('Clinical Notes')}
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Chief Complaint</label>
            <textarea style={{ ...inputStyle, height: 60, resize: 'vertical' }}
              placeholder="Patient's main complaint..."
              value={form.chief_complaint} onChange={e => setForm({...form, chief_complaint: e.target.value})} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Diagnosis</label>
            <textarea style={{ ...inputStyle, height: 60, resize: 'vertical' }}
              placeholder="Diagnosis..."
              value={form.diagnosis} onChange={e => setForm({...form, diagnosis: e.target.value})} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Doctor Notes</label>
            <textarea style={{ ...inputStyle, height: 60, resize: 'vertical' }}
              placeholder="Additional notes..."
              value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: 13, cursor: 'pointer', color: '#475569', fontWeight: 600 }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 2, padding: 10, borderRadius: 8, background: loading ? '#94a3b8' : 'linear-gradient(135deg, #0ea5e9, #0284c7)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {loading ? 'Saving...' : 'Save Visit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Add Prescription Modal ────────────────────────────
function AddPrescriptionModal({ patient, visitId, onClose, onSuccess }) {
  const EMPTY_MED = { name: '', dosage: '', frequency: '', duration: '', food: 'After food' };
  const [medications, setMedications] = useState([{ ...EMPTY_MED }]);
  const [form, setForm] = useState({ doctor_name: '', instructions: '', diagnosis: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const addMed    = () => setMedications([...medications, { ...EMPTY_MED }]);
  const removeMed = (i) => setMedications(medications.filter((_, idx) => idx !== i));
  const updateMed = (i, field, value) => {
    const updated = [...medications];
    updated[i] = { ...updated[i], [field]: value };
    setMedications(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const headers = await getAuthHeader();
      const res = await axios.post(`${API_URL}/prescriptions`, {
        ...form,
        patient_id:  patient.patient_id,
        hospital_id: patient.hospital_id,
        visit_id:    visitId,
        medications: medications.filter(m => m.name.trim()),
      }, { headers });
      const data = parseResponse(res);
      onSuccess(data);
    } catch (err) {
      setError('Failed to create prescription');
    } finally { setLoading(false); }
  };

  const medInputStyle = { ...inputStyle, padding: '6px 8px', fontSize: 12 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} className="modal-overlay">
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 700, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Add Prescription</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 20 }}>✕</span>
        </div>
        {error && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Doctor Name</label>
              <input style={inputStyle} placeholder="Dr. Name" required
                value={form.doctor_name} onChange={e => setForm({...form, doctor_name: e.target.value})} />
            </div>
            <div>
              <label style={labelStyle}>Diagnosis</label>
              <input style={inputStyle} placeholder="Diagnosis"
                value={form.diagnosis} onChange={e => setForm({...form, diagnosis: e.target.value})} />
            </div>
          </div>

          {sectionTitle('Medications')}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 1.5fr 2fr 1.5fr 1.5fr 0.5fr', gap: 6, marginBottom: 6 }}>
              {['Medicine Name', 'Dosage', 'Frequency', 'Duration', 'Food', ''].map((h, i) => (
                <div key={i} style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</div>
              ))}
            </div>
            {medications.map((med, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1.5fr 2fr 1.5fr 1.5fr 0.5fr', gap: 6, marginBottom: 6 }}>
                <input style={medInputStyle} placeholder="e.g. Paracetamol 500mg"
                  value={med.name} onChange={e => updateMed(i, 'name', e.target.value)} />
                <input style={medInputStyle} placeholder="e.g. 500mg"
                  value={med.dosage} onChange={e => updateMed(i, 'dosage', e.target.value)} />
                <select style={medInputStyle} value={med.frequency} onChange={e => updateMed(i, 'frequency', e.target.value)}>
                  <option>Once daily</option>
                  <option>Twice daily</option>
                  <option>Thrice daily</option>
                  <option>Four times daily</option>
                  <option>Every 6 hours</option>
                  <option>Every 8 hours</option>
                  <option>As needed</option>
                </select>
                <input style={medInputStyle} placeholder="e.g. 5 days"
                  value={med.duration} onChange={e => updateMed(i, 'duration', e.target.value)} />
                <select style={medInputStyle} value={med.food} onChange={e => updateMed(i, 'food', e.target.value)}>
                  <option>After food</option>
                  <option>Before food</option>
                  <option>With food</option>
                  <option>Empty stomach</option>
                </select>
                <button type="button" onClick={() => removeMed(i)}
                  style={{ background: '#fee2e2', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#dc2626', fontSize: 14, fontWeight: 700 }}>✕</button>
              </div>
            ))}
            <button type="button" onClick={addMed}
              style={{ marginTop: 8, padding: '6px 14px', borderRadius: 8, background: '#f0f9ff', border: '1px solid #bae6fd', fontSize: 12, cursor: 'pointer', color: '#0284c7', fontWeight: 600 }}>
              + Add Medicine
            </button>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Special Instructions</label>
            <textarea style={{ ...inputStyle, height: 70, resize: 'vertical' }}
              placeholder="e.g. Drink plenty of water, avoid spicy food..."
              value={form.instructions} onChange={e => setForm({...form, instructions: e.target.value})} />
          </div>

          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: '#0284c7', fontWeight: 600 }}>
              📄 PDF will be generated and sent to patient via WhatsApp
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: 13, cursor: 'pointer', color: '#475569', fontWeight: 600 }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 2, padding: 10, borderRadius: 8, background: loading ? '#94a3b8' : 'linear-gradient(135deg, #0ea5e9, #0284c7)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {loading ? 'Generating PDF...' : '📄 Save & Send Prescription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Upload Lab Report Modal ───────────────────────────
function UploadLabReportModal({ patient, onClose, onSuccess }) {
  const [form, setForm]     = useState({ report_name: '', report_date: new Date().toISOString().split('T')[0] });
  const [file, setFile]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select a file'); return; }
    setLoading(true); setError('');
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(',')[1];
        const headers = await getAuthHeader();
        const res = await axios.post(`${API_URL}/lab-reports`, {
          ...form,
          patient_id:   patient.patient_id,
          hospital_id:  patient.hospital_id,
          file_content: base64,
          file_name:    file.name,
          file_type:    file.type,
        }, { headers });
        const data = parseResponse(res);
        onSuccess(data);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to upload report');
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} className="modal-overlay">
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Upload Lab Report</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 20 }}>✕</span>
        </div>
        {error && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Report Name</label>
            <input style={inputStyle} placeholder="e.g. Blood CBC, X-Ray Chest" required
              value={form.report_name} onChange={e => setForm({...form, report_name: e.target.value})} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Report Date</label>
            <input style={inputStyle} type="date" required
              value={form.report_date} onChange={e => setForm({...form, report_date: e.target.value})} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>File (PDF, Image)</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile}
              style={{ ...inputStyle, padding: '7px 12px' }} required />
            {file && <div style={{ fontSize: 12, color: '#16a34a', marginTop: 6 }}>✅ {file.name}</div>}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: 13, cursor: 'pointer', color: '#475569', fontWeight: 600 }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 2, padding: 10, borderRadius: 8, background: loading ? '#94a3b8' : 'linear-gradient(135deg, #0ea5e9, #0284c7)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {loading ? 'Uploading...' : '⬆ Upload Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Patient Record Component ─────────────────────
export default function PatientRecord({ patient, onClose, user }) {
  const [activeTab, setActiveTab]         = useState('overview');
  const [visits, setVisits]               = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [labReports, setLabReports]       = useState([]);
  const [loading, setLoading]             = useState(false);
  const [showAddVisit, setShowAddVisit]   = useState(false);
  const [showAddRx, setShowAddRx]         = useState(false);
  const [showUploadLab, setShowUploadLab] = useState(false);
  const [selectedVisitId, setSelectedVisitId] = useState(null);
  const [successMsg, setSuccessMsg]       = useState('');

  const canEdit = ['super-admin', 'hospital-admin', 'reception', 'doctor'].includes(user?.role);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.patient_id]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeader();
      const [vRes, pRes, lRes] = await Promise.all([
        axios.get(`${API_URL}/visits`,       { headers, params: { patient_id: patient.patient_id } }),
        axios.get(`${API_URL}/prescriptions`,{ headers, params: { patient_id: patient.patient_id } }),
        axios.get(`${API_URL}/lab-reports`,  { headers, params: { patient_id: patient.patient_id } }),
      ]);
      setVisits(parseResponse(vRes).visits || []);
      setPrescriptions(parseResponse(pRes).prescriptions || []);
      setLabReports(parseResponse(lRes).lab_reports || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  const tabs = [
    { key: 'overview',      label: '👤 Overview' },
    { key: 'visits',        label: `🏥 Visits (${visits.length})` },
    { key: 'prescriptions', label: `💊 Prescriptions (${prescriptions.length})` },
    { key: 'lab_reports',   label: `🧪 Lab Reports (${labReports.length})` },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '90vw', maxWidth: 900, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ padding: '20px 28px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0f9ff', borderRadius: '16px 16px 0 0' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>{patient.name}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {patient.patient_id} · {patient.phone} · {patient.gender}, {patient.age} yrs
              {patient.blood_group && ` · ${patient.blood_group}`}
            </div>
          </div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 24 }}>✕</span>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '12px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              padding: '6px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', border: 'none',
              background: activeTab === t.key ? '#0ea5e9' : 'transparent',
              color: activeTab === t.key ? '#fff' : '#64748b',
            }}>{t.label}</button>
          ))}
        </div>

        {/* Success message */}
        {successMsg && (
          <div style={{ background: '#dcfce7', color: '#16a34a', padding: '10px 20px', fontSize: 13, fontWeight: 600 }}>
            ✅ {successMsg}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading patient record...</div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div>
                  <div className="grid-3" style={{ marginBottom: 24 }}>
                    {[
                      { label: 'Total Visits',    value: visits.length,        color: '#0ea5e9' },
                      { label: 'Prescriptions',   value: prescriptions.length, color: '#10b981' },
                      { label: 'Lab Reports',     value: labReports.length,    color: '#8b5cf6' },
                    ].map((s, i) => (
                      <div key={i} style={{ background: '#f8fafc', borderRadius: 12, padding: '16px 20px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  <div className="grid-2">
                    {[
                      { label: 'Full Name',    value: patient.name },
                      { label: 'Phone',        value: patient.phone },
                      { label: 'Age',          value: patient.age },
                      { label: 'Gender',       value: patient.gender },
                      { label: 'Blood Group',  value: patient.blood_group || '—' },
                      { label: 'Address',      value: patient.address || '—' },
                      { label: 'Condition',    value: patient.condition || '—' },
                      { label: 'Registered',   value: patient.created_at?.slice(0, 10) },
                    ].map((item, i) => (
                      <div key={i} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{item.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Last visit summary */}
                  {visits.length > 0 && (
                    <div style={{ marginTop: 20, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 12, padding: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#0284c7', marginBottom: 8 }}>LAST VISIT</div>
                      <div style={{ fontSize: 13, color: '#1e293b' }}>
                        <strong>{visits[0].visit_date}</strong> · Dr. {visits[0].doctor_name}
                        {visits[0].diagnosis && <span> · {visits[0].diagnosis}</span>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Visits Tab */}
              {activeTab === 'visits' && (
                <div>
                  {canEdit && (
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                      <button onClick={() => setShowAddVisit(true)} style={{
                        background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
                        color: '#fff', border: 'none', borderRadius: 8,
                        padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer'
                      }}>+ Record Visit</button>
                    </div>
                  )}
                  {visits.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>No visits recorded yet</div>
                  ) : visits.map((v, i) => (
                    <div key={i} style={{ background: '#f8fafc', borderRadius: 12, padding: 16, marginBottom: 12, border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{v.visit_date}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>Dr. {v.doctor_name} · {v.visit_id}</div>
                        </div>
                        {canEdit && (
                          <button onClick={() => { setSelectedVisitId(v.visit_id); setShowAddRx(true); }} style={{
                            background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8,
                            padding: '5px 12px', fontSize: 11, cursor: 'pointer', color: '#16a34a', fontWeight: 600
                          }}>💊 Add Prescription</button>
                        )}
                      </div>

                      {/* Vitals */}
                      {v.vitals && Object.values(v.vitals).some(x => x) && (
                        <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                          {[
                            { label: 'BP',     value: v.vitals.bp },
                            { label: 'Temp',   value: v.vitals.temp },
                            { label: 'Weight', value: v.vitals.weight },
                            { label: 'Pulse',  value: v.vitals.pulse },
                            { label: 'SpO2',   value: v.vitals.spo2 },
                          ].filter(x => x.value).map((x, j) => (
                            <div key={j} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
                              <span style={{ color: '#94a3b8', fontWeight: 600 }}>{x.label}: </span>
                              <span style={{ color: '#1e293b', fontWeight: 600 }}>{x.value}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {v.chief_complaint && (
                        <div style={{ marginBottom: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Complaint: </span>
                          <span style={{ fontSize: 12, color: '#475569' }}>{v.chief_complaint}</span>
                        </div>
                      )}
                      {v.diagnosis && (
                        <div style={{ marginBottom: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Diagnosis: </span>
                          <span style={{ fontSize: 12, color: '#1e293b', fontWeight: 600 }}>{v.diagnosis}</span>
                        </div>
                      )}
                      {v.notes && (
                        <div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Notes: </span>
                          <span style={{ fontSize: 12, color: '#475569' }}>{v.notes}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Prescriptions Tab */}
              {activeTab === 'prescriptions' && (
                <div>
                  {prescriptions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>No prescriptions yet</div>
                  ) : prescriptions.map((rx, i) => (
                    <div key={i} style={{ background: '#f8fafc', borderRadius: 12, padding: 16, marginBottom: 12, border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{rx.prescribed_date}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>Dr. {rx.doctor_name} · {rx.prescription_id}</div>
                          {rx.diagnosis && <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>Diagnosis: {rx.diagnosis}</div>}
                        </div>
                        {rx.pdf_url && (
                          <a href={rx.pdf_url} target="_blank" rel="noreferrer" style={{
                            background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
                            color: '#fff', padding: '6px 14px', borderRadius: 8,
                            fontSize: 12, fontWeight: 600, textDecoration: 'none'
                          }}>📄 Download PDF</a>
                        )}
                      </div>
                      {rx.medications?.length > 0 && (
                        <div className="table-wrap"><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: '#e0f2fe' }}>
                              {['Medicine', 'Dosage', 'Frequency', 'Duration', 'Food'].map(h => (
                                <th key={h} style={{ padding: '5px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#0284c7', textTransform: 'uppercase' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rx.medications.map((m, j) => (
                              <tr key={j} style={{ borderTop: '1px solid #e2e8f0', background: j % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                <td style={{ padding: '6px 10px', fontWeight: 600 }}>{m.name}</td>
                                <td style={{ padding: '6px 10px', color: '#475569' }}>{m.dosage}</td>
                                <td style={{ padding: '6px 10px', color: '#475569' }}>{m.frequency}</td>
                                <td style={{ padding: '6px 10px', color: '#475569' }}>{m.duration}</td>
                                <td style={{ padding: '6px 10px', color: '#475569' }}>{m.food}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table></div>
                      )}
                      {rx.instructions && (
                        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fefce8', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                          📋 {rx.instructions}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Lab Reports Tab */}
              {activeTab === 'lab_reports' && (
                <div>
                  {canEdit && (
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                      <button onClick={() => setShowUploadLab(true)} style={{
                        background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
                        color: '#fff', border: 'none', borderRadius: 8,
                        padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer'
                      }}>⬆ Upload Lab Report</button>
                    </div>
                  )}
                  {labReports.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>No lab reports uploaded yet</div>
                  ) : (
                    <div className="grid-2">
                      {labReports.map((r, i) => (
                        <div key={i} style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>🧪 {r.report_name}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{r.report_date} · {r.report_id}</div>
                          </div>
                          {r.url && (
                            <a href={r.url} target="_blank" rel="noreferrer" style={{
                              background: '#f0f9ff', border: '1px solid #bae6fd',
                              color: '#0284c7', padding: '5px 12px', borderRadius: 8,
                              fontSize: 11, fontWeight: 600, textDecoration: 'none'
                            }}>View</a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Sub-modals */}
      {showAddVisit && (
        <AddVisitModal
          patient={patient} user={user}
          onClose={() => setShowAddVisit(false)}
          onSuccess={(data) => {
            setShowAddVisit(false);
            showSuccess('Visit recorded successfully');
            fetchAll();
            setActiveTab('visits');
          }}
        />
      )}
      {showAddRx && (
        <AddPrescriptionModal
          patient={patient} visitId={selectedVisitId}
          onClose={() => { setShowAddRx(false); setSelectedVisitId(null); }}
          onSuccess={(data) => {
            setShowAddRx(false);
            showSuccess('Prescription created and sent to patient via WhatsApp');
            fetchAll();
            setActiveTab('prescriptions');
          }}
        />
      )}
      {showUploadLab && (
        <UploadLabReportModal
          patient={patient}
          onClose={() => setShowUploadLab(false)}
          onSuccess={(data) => {
            setShowUploadLab(false);
            showSuccess('Lab report uploaded successfully');
            fetchAll();
            setActiveTab('lab_reports');
          }}
        />
      )}
    </div>
  );
}